import { useState } from 'react';
import { NJText } from '@engie-group/fluid-design-system-react';
import {
  UMAppShell,
  UMButton,
  UMFormStyles,
  UMMap,
  UMMessage,
  UMR,
  UMMapToolbar,
  UM_RAIL_ITEMS,
} from '../_components';
import { useSatelliteMap } from './hooks';
import { BasemapControls } from './BasemapControls';
import { LYON_BOUNDS, LYON_CENTER } from './satelliteStyle';
import type { SatelliteStatus } from './types';

export const meta = {
  title: 'Satellite pur',
  description: 'Fond Satellite Urban Map avec une case Étiquettes pour passer à l’imagerie pure.',
  tags: ['map', 'territory', 'poc'],
  author: 'erika.dalmonte',
  status: 'draft',
};

const RAIL_ITEMS = UM_RAIL_ITEMS.map((item) => ({ ...item, disabled: true }));
const EMPTY_SELECTIONS: string[] = [];
const MESSAGES: Record<SatelliteStatus, { title: string; body: string }> = {
  offline: {
    title: 'Mode hors ligne — satellite indisponible',
    body: 'Le fond clair est un repère de démonstration, pas une image satellite. Configurez VITE_MAPBOX_TOKEN dans app/front/.env.local puis redémarrez le serveur.',
  },
  loading: {
    title: 'Chargement du fond de carte…',
    body: 'Le fond sélectionné est en cours de chargement.',
  },
  ready: {
    title: 'Satellite pur',
    body: 'Sans routes dessinées ni libellés. Les rues visibles dans la photographie restent présentes.',
  },
  error: {
    title: 'Impossible de charger le fond de carte',
    body: 'Vérifiez la connexion, la validité du token Mapbox et ses restrictions d’URL. Aucune fausse imagerie n’est affichée.',
  },
};

export default function SatellitePur() {
  const { mapRef, status, hasToken, retry, basemap, selectBasemap, labels, setLabels } = useSatelliteMap();
  const [is3d, setIs3d] = useState(false);
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const message = MESSAGES[status];
  const recenter = () => {
    setIs3d(false);
    mapRef.current?.flyTo(LYON_CENTER, 14);
  };

  return (
    <UMAppShell
      title="Urban Map — Satellite pur"
      railItems={RAIL_ITEMS}
      toolbar={
        <UMMapToolbar
          selections={EMPTY_SELECTIONS}
          search={search}
          onSearchChange={setSearch}
          onCreateFolder={() => {
            setNotice('La création de sélection n’est pas simulée dans ce prototype.');
            window.setTimeout(() => setNotice(null), 3000);
          }}
          onLocate={recenter}
        />
      }
    >
      <UMFormStyles />
      <UMMap ref={mapRef} initialBounds={LYON_BOUNDS} is3d={is3d} />
      {(status === 'loading' || status === 'error') && (
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: UMR.map.fallbackWater, zIndex: 1 }} />
      )}
      {status !== 'ready' && <div
        style={{
          position: 'absolute', top: UMR.size.toolbarHeight + UMR.size.controlsInset,
          left: UMR.size.controlsInset, right: UMR.size.controlsInset,
          maxWidth: 'min(440px, calc(100% - 32px))', zIndex: 5,
        }}
      >
        <UMMessage title={<NJText as="strong">{message.title}</NJText>}>
          <NJText>{message.body}</NJText>
          {status === 'error' && <UMButton onClick={retry}>Réessayer</UMButton>}
        </UMMessage>
      </div>}
      <BasemapControls
        value={basemap}
        hasToken={hasToken}
        onChange={selectBasemap}
        labels={labels}
        onLabelsChange={setLabels}
        onZoomIn={() => mapRef.current?.zoomIn()}
        onZoomOut={() => mapRef.current?.zoomOut()}
        is3d={is3d}
        onToggle3d={setIs3d}
      />
      {notice && (
        <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 30 }}>
          <UMMessage>{notice}</UMMessage>
        </div>
      )}
    </UMAppShell>
  );
}
